import { useLearningUiTranslation } from '@features/learning/shared/use-learning-ui-translation'
import { useWarmLearningFeedbackAudio } from '@features/learning/shared/learning-feedback-audio'
import type { ReactElement } from 'react'
import { AppDialog, AppEmptyState } from '@shared/ui'
import { LearningTemplateGameShell } from '@features/learning/shared/templates'
import { useFlashcardsState } from '@features/flashcards/state/useFlashcardsState'
import type { FlashcardPrompt } from '@features/flashcards/state/flashcards-state'
import { FlashcardGameResultScreen } from './FlashcardGameResultScreen'
import { Template1, Template2, Template3, Template4, Template5 } from './templates'
import type { FlashcardGameTemplateProps } from './template-types'
import './flashcard-game.css'

const templateById = {
  1: Template1,
  2: Template2,
  3: Template3,
  4: Template4,
  5: Template5,
} satisfies Record<FlashcardPrompt['template'], (props: FlashcardGameTemplateProps) => ReactElement>

export function FlashcardGameScreen() {
  useWarmLearningFeedbackAudio()
  const ui = useLearningUiTranslation()
  const { actions, state } = useFlashcardsState()
  const session = state.gameSession

  const handleClose = () => {
    actions.resetGameSession()
  }

  if (!session) {
    return null
  }

  if (session.status === 'finished') {
    return (
      <AppDialog dividers={false} fullScreen onClose={handleClose} open>
        <FlashcardGameResultScreen onReturn={handleClose} session={session} />
      </AppDialog>
    )
  }

  const activePrompt = session.prompts[session.activeIndex]

  if (!activePrompt) {
    return (
      <AppDialog dividers={false} fullScreen onClose={handleClose} open>
        <LearningTemplateGameShell
          closeLabel={ui("Close flashcard game")}
          onClose={handleClose}
          progress={0}
          progressLabel={ui("Flashcard game progress")}
          title={ui("Flashcard game")}
        >
          <AppEmptyState
            message={ui("Close this dialog and start a new game session from the flashcards collection.")}
            title={ui("No prompt available")}
          />
        </LearningTemplateGameShell>
      </AppDialog>
    )
  }

  const ActiveTemplate = templateById[activePrompt.template]
  const isLastPrompt = session.activeIndex >= session.prompts.length - 1
  const progress = Math.round(((session.activeIndex + 1) / session.prompts.length) * 100)

  return (
    <AppDialog dividers={false} fullScreen onClose={handleClose} open>
      <LearningTemplateGameShell
        closeLabel={ui("Close flashcard game")}
        onClose={handleClose}
        progress={progress}
        progressLabel={ui("Flashcard game progress")}
        title={ui('Flashcard game - {{level}}', { level: ui(session.level) })}
      >
        <ActiveTemplate
          key={activePrompt.id}
          isLastPrompt={isLastPrompt}
          onContinue={(result) => {
            actions.scoreCurrentPrompt(result)
            if (isLastPrompt) {
              actions.finishGameSession()
              return
            }
            actions.setGameActiveIndex(session.activeIndex + 1)
          }}
          prompt={activePrompt}
        />
      </LearningTemplateGameShell>
    </AppDialog>
  )
}
